import { FormattedMessage } from 'react-intl';
import { DefaultWebViewOptions, DefaultSystemBrowserOptions, InAppBrowser } from '@capacitor/inappbrowser';
import { MouseEvent, useEffect, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { PluginListenerHandle } from '@capacitor/core';
import { Box, Divider, Stack, Skeleton } from '@mui/material';

import { Paper, SizingMultiplier, Typography, VerifiedButton } from '@ocx/ui';
import { logInDev } from '@ocx/utils';
import { isNativePlatform, isAndroidPlatform } from '@ocx/utils-device';
import { useMembership } from '@ocx/data-membership';
import { ExternalIdentityVerificationProvider } from '@ocx/graphql';

import { getVerificationStatus } from '../../external-identity-verification.utils';
import { useGenerateExternalIdentityVerificationParams } from '../../use-generate-external-identity-verification-params.hook';
import { messages } from './idme.messages';
import {
  IDME_ELEMENT_ID,
  IDME_VERIFY_BUTTON_IMG_ALT,
  IDME_VERIFY_BUTTON_IMG_SRC,
  IDME_VERIFY_BUTTON_IMG_STYLES,
  IDME_WEB_CLOSED_CHECK_INTERVAL_MS,
} from './idme.constants';
import { getIsIdmeVerificationCloseUrl } from './idme.utils';

export const IDMEProvider = () => {
  const { refetch: refetchMembership, membership } = useMembership();
  const params = useGenerateExternalIdentityVerificationParams();
  const inAppBrowserClosedListenerRef = useRef<PluginListenerHandle | null>(null);
  const inAppBrowserNavigatedListenerRef = useRef<PluginListenerHandle | null>(null);
  const webIntervalIdRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      inAppBrowserClosedListenerRef.current?.remove().catch(logInDev);
      inAppBrowserNavigatedListenerRef.current?.remove().catch(logInDev);
      clearInterval(webIntervalIdRef.current);
    };
  }, []);

  const verification = useMemo(
    () =>
      getVerificationStatus(membership.externalIdentityVerificationProfiles, ExternalIdentityVerificationProvider.Idme),
    [membership.externalIdentityVerificationProfiles],
  );

  if (params.loading) {
    return <Skeleton id={IDME_ELEMENT_ID} variant="rounded" height={120} />;
  }

  if (!params.data) return null;

  const handleClick = async (e: MouseEvent<HTMLAnchorElement>) => {
    try {
      if (!params.data) return;
      e.preventDefault();

      // For native platforms, use InAppBrowser to open the authorization URL
      if (isNativePlatform()) {
        Sentry.addBreadcrumb({ message: 'IDMEProvider: Opening InAppBrowser for ID.me verification' });

        inAppBrowserClosedListenerRef.current = await InAppBrowser.addListener('browserClosed', () => {
          Sentry.addBreadcrumb({ message: 'IDMEProvider: InAppBrowser closed, refetching membership' });
          refetchMembership().catch(logInDev);
        });

        // window.close() is not supported in WebView when opened via InAppBrowser,
        // so we use a listener to handle the result URL navigation and close the InAppBrowser
        inAppBrowserNavigatedListenerRef.current = await InAppBrowser.addListener(
          'browserPageNavigationCompleted',
          (data) => {
            Sentry.addBreadcrumb({ message: 'IDMEProvider: InAppBrowser navigation completed' });
            if (!data.url) return;

            if (getIsIdmeVerificationCloseUrl(data.url)) {
              Sentry.addBreadcrumb({
                message: 'IDMEProvider: ID.me verification close URL detected, closing InAppBrowser',
              });
              InAppBrowser.close();
            }
          },
        );

        // Temporary workaround for Android to open the URL in the system browser
        // because of issues with some ID.me flows in WebView on Android
        if (isAndroidPlatform()) {
          await InAppBrowser.openInSystemBrowser({
            url: params.data.authorizationUrl,
            options: DefaultSystemBrowserOptions,
          });
          return;
        }

        await InAppBrowser.openInWebView({
          url: params.data.authorizationUrl,
          options: DefaultWebViewOptions,
        });
        return;
      }

      // For web platform, open the authorization URL in a new tab
      Sentry.addBreadcrumb({ message: 'IDMEProvider: Opening web window for ID.me verification' });
      const webWindow = window.open(params.data.authorizationUrl, '_blank');
      if (!webWindow) {
        Sentry.addBreadcrumb({ message: 'IDMEProvider: Failed to open popup window (Web)' });
        return;
      }
      Sentry.addBreadcrumb({
        message: 'IDMEProvider: Setting interval to check web window closure for ID.me verification',
      });
      webIntervalIdRef.current = setInterval(() => {
        if (!webWindow.closed) return;
        Sentry.addBreadcrumb({ message: 'IDMEProvider: Web window closed, refetching membership' });
        clearInterval(webIntervalIdRef.current);
        refetchMembership().catch(logInDev);
      }, IDME_WEB_CLOSED_CHECK_INTERVAL_MS);
    } catch (error) {
      logInDev(error);
      Sentry.captureException(error);
    }
  };

  return (
    <Stack component={Paper} divider={verification.isVerified ? <Divider /> : undefined} spacing={SizingMultiplier.md}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={SizingMultiplier.md}>
        <Box>
          <Typography variant="h5" textTransform="none" gutterBottom id={IDME_ELEMENT_ID}>
            <FormattedMessage {...messages['idme:profile-section:title']} />
          </Typography>
          <Typography>
            {verification.isVerified ? (
              <FormattedMessage
                {...messages['idme:verified-status-message']}
                values={{ statuses: verification.verifiedStatusesString }}
              />
            ) : (
              <FormattedMessage {...messages['idme:profile-section:description']} />
            )}
          </Typography>
        </Box>

        {verification.isVerified && (
          <Box>
            <VerifiedButton isVerified />
          </Box>
        )}
      </Stack>
      <a href={params.data.authorizationUrl} onClick={handleClick}>
        <img style={IDME_VERIFY_BUTTON_IMG_STYLES} src={IDME_VERIFY_BUTTON_IMG_SRC} alt={IDME_VERIFY_BUTTON_IMG_ALT} />
      </a>
    </Stack>
  );
};
